import { useCallback, useState } from 'react';

export const READING_THEMES = ['light', 'sepia', 'dark'] as const;
export type ReadingTheme = (typeof READING_THEMES)[number];
/** Text size steps, as a zoom factor of the exam view. */
export const READING_ZOOMS = [0.9, 1, 1.1, 1.25, 1.4] as const;

export interface ReadingPrefs {
  theme: ReadingTheme;
  zoom: number;
}

const KEY = 'exam.readingPrefs';
const DEFAULT: ReadingPrefs = { theme: 'light', zoom: 1 };

export function loadReadingPrefs(): ReadingPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<ReadingPrefs> | null;
    return {
      theme: READING_THEMES.includes(raw?.theme as ReadingTheme) ? (raw!.theme as ReadingTheme) : DEFAULT.theme,
      zoom: (READING_ZOOMS as readonly number[]).includes(raw?.zoom as number) ? (raw!.zoom as number) : DEFAULT.zoom,
    };
  } catch {
    return DEFAULT;
  }
}

/** The learner's reading theme and text size, kept in this browser for every exam. */
export function useReadingPrefs() {
  const [prefs, setPrefs] = useState<ReadingPrefs>(loadReadingPrefs);
  const update = useCallback((patch: Partial<ReadingPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        // Private mode: the choice lasts for this page only.
      }
      return next;
    });
  }, []);
  const step = useCallback(
    (direction: 1 | -1) => {
      const zooms = READING_ZOOMS as readonly number[];
      const at = zooms.indexOf(prefs.zoom);
      const next = zooms[Math.min(zooms.length - 1, Math.max(0, (at < 0 ? 1 : at) + direction))];
      update({ zoom: next });
    },
    [prefs.zoom, update],
  );
  return { prefs, update, zoomIn: () => step(1), zoomOut: () => step(-1) };
}
